import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import { TextField } from 'components/styledFormElements/TextField'
import { ToggleField } from 'components/styledFormElements/ToggleField'
import { Button } from 'components/ui/button'
import { Loader } from 'components/loader'
import { DialogWrapper } from 'components/dialogWrapper'
import { useNotificationBar } from 'context/notificationContext'
import { getErrorResponse } from 'services/util'

import MultiSelect from 'components/MultiSelect/MultiSelect'
import { useGetWidgetTypesData } from 'hooks/widgets'


import {
  useAddTemplatesTypeData,
  useUpdateTemplatesTypeData,
} from '../../../../hooks/templatesType'

interface Props {
  isEditForm: boolean
  input_data: any
  isOpen: boolean
  setIsOpen: (v: boolean) => void
  onSubmitHandler?: Function
}

/* ✅ Validation */
/* 🔥 CREATE FORM VALIDATION */
const createSchema = yup.object({
  name: yup.string().required('Name is required'),
  code: yup.string().required('Code is required'),
  widget_code: yup.string().required('Widget code is required'),
  description: yup.string(),
  status: yup.boolean(),
})

/* 🔥 EDIT FORM VALIDATION (NO REQUIRED FIELDS) */
const editSchema = yup.object({
  name: yup.string(),          // ❌ no validation
  code: yup.string(),          // ❌ no validation
  widget_code: yup.string(),   // ❌ no validation
  description: yup.string(),   // ❌ no validation
  status: yup.boolean(),
})


export const AddUpdate = ({
  isEditForm,
  input_data,
  isOpen,
  setIsOpen,
  onSubmitHandler,
}: Props) => {
  const notification = useNotificationBar()

  const [selectedWidgets, setSelectedWidgets] = useState<string[]>([])
  const [widgetError, setWidgetError] = useState<string>('')


const {
  register,
  reset,
  watch,
  handleSubmit,
  formState: { errors, dirtyFields },
} = useForm({
  resolver: yupResolver(isEditForm ? editSchema : createSchema),  // 🔥 FIX
  mode: 'onTouched',
})

  const { data: widgetsRes, isLoading: isWidgetsLoading } =
    useGetWidgetTypesData()

  const widgetList = Array.isArray(widgetsRes?.data?.items)
    ? widgetsRes.data.items
    : []

  const widgetOptions = widgetList.map((w: any) => ({
    label: w.name,
    value: w.code,
  }))



  const { mutate: addTemplateType, isLoading: isAdding } =
    useAddTemplatesTypeData()
  const { mutate: updateTemplateType, isLoading: isUpdating } =
    useUpdateTemplatesTypeData()

  const isLoading = isAdding || isUpdating

  useEffect(() => {
    if (input_data) {
      console.log('input_data:', input_data);
      reset({
        name: input_data.name,
        code: input_data.code || input_data.template_code,          // 🔥 FIX
        widget_code: input_data.widget_code || input_data.template_type, // 🔥 FIX
        description: input_data.description,
        status: !!input_data?.status,
      })

      if (Array.isArray(input_data?.widgetsCodes?.widgets)) {
        setSelectedWidgets(input_data.widgetsCodes.widgets)
      }
    }
  }, [input_data, reset])





  const onSubmit = (values: any) => {
    debugger;
    /* ✅ Widget validation */
    if (!selectedWidgets.length) {
      setWidgetError('At least one widget is required')
      return
    }

    setWidgetError('')

    const payload = {
      ...values,
      status: values.status ? 1 : 0,
      widgetsCodes: {
        widgets: selectedWidgets,
      },
    }

    if (isEditForm && input_data?.id) {
      // 🔥 ABP requires id inside body + only editable fields
      const updated = {
        //id: input_data.id,

        name: values.name,
        code: values.code,                    // 🔥 REQUIRED
        widget_code: values.widget_code,      // 🔥 REQUIRED
        description: values.description,
        status: values.status ? 1 : 0,

        widgetsCodes: {
          widgets: selectedWidgets,
        },
      }

      updateTemplateType(
        {
          id: input_data.id,        // 🔥 id in URL
          data: updated,           // 🔥 body in data
        },
        {
          onSuccess: () => {
            notification.push({
              title: 'Success',
              message: 'Template type updated successfully',
              type: 'success',
            })
            onSubmitHandler?.()
            setIsOpen(false)
          },
          onError: (err) =>
            notification.push(getErrorResponse(err)),
        }
      )
    }
    else {
      addTemplateType(payload, {
        onSuccess: () => {
          notification.push({
            title: 'Success',
            message: 'Template type created successfully',
            type: 'success',
          })
          onSubmitHandler?.()
          setIsOpen(false)
        },
        onError: (err) =>
          notification.push(getErrorResponse(err)),
      })
    }
  }

  return (
    <DialogWrapper
      title={
        isEditForm
          ? 'Update Template Type'
          : 'Create Template Type'
      }
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        <TextField
          register={register('name')}
          errors={errors}
          label="Name"
          disabled={isEditForm}          // 🔥 READ ONLY IN EDIT
        />
        <TextField
          register={register('code')}
          errors={errors}
          label="Code"
          disabled={isEditForm}          // 🔥 READ ONLY IN EDIT
        />
        <TextField
          register={register('widget_code')}
          errors={errors}
          label="Widget Code"
          disabled={isEditForm}          // 🔥 READ ONLY IN EDIT
        />
        <TextField
          register={register('description')}
          errors={errors}
          label="Description"
          disabled={isEditForm}          // 🔥 READ ONLY IN EDIT
        />

        {/* ✅ MULTI SELECT */}
        <div className="col-span-full">
          <MultiSelect
            label="Widgets"
            options={widgetOptions}
            value={selectedWidgets}
            onChange={setSelectedWidgets}
            error={widgetError}
            placeholder={
              isWidgetsLoading ? 'Loading widgets...' : 'Search widget...'
            }
          />
        </div>

        <ToggleField
          register={register('status')}
          label="Status"
          display={watch('status') ? 'Active' : 'Inactive'}
        />

        <div className="col-span-full flex justify-end gap-2">
          <Button
            type="button"
            color="basic"
            onClick={() => setIsOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            color="blue"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader /> Saving…
              </>
            ) : (
              'Save'
            )}
          </Button>
        </div>
      </form>
    </DialogWrapper>
  )
}
